import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Building2, Plus, Search, MapPin, Edit, Trash2, FileText } from "lucide-react";
import { toast } from "sonner";

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async (query = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/clients", { params: query ? { q: query } : {} });
      setClients(data);
    } catch (e) {
      toast.error("Errore nel caricamento clienti");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleEdit = (e, c) => {
    e.stopPropagation();
    navigate(`/clienti/${c.id}`);
  };

  const handleNewOffer = (e, c) => {
    e.stopPropagation();
    navigate(`/offerte/nuova?client_id=${c.id}`);
  };

  const handleDelete = async (e, c) => {
    e.stopPropagation();
    if (!window.confirm(`Eliminare il cliente "${c.ragione_sociale}"? L'operazione fallirà se ci sono offerte collegate.`)) return;
    try {
      await api.delete(`/clients/${c.id}`);
      toast.success("Cliente eliminato");
      load(q);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore eliminazione");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-mono">Anagrafica</div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-[#0F2C59]">Clienti</h1>
        </div>
        <Button
          onClick={() => navigate("/clienti/nuovo")}
          data-testid="new-client-btn"
          className="bg-[#0F2C59] hover:bg-[#173F7A]"
        >
          <Plus className="w-4 h-4 mr-1" /> Nuovo cliente
        </Button>
      </div>

      <Card className="p-4">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="client-search"
            placeholder="Cerca per ragione sociale, P.IVA, comune…"
            value={q}
            onChange={(e) => { setQ(e.target.value); load(e.target.value); }}
            className="pl-10"
          />
        </div>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading && <div className="col-span-full text-slate-400 py-12 text-center">Caricamento…</div>}
        {!loading && clients.length === 0 && (
          <Card className="col-span-full p-12 text-center">
            <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <div className="text-slate-500 mb-4">Nessun cliente presente</div>
            <Button onClick={() => navigate("/clienti/nuovo")} className="bg-[#0F2C59]" data-testid="empty-new-client">
              <Plus className="w-4 h-4 mr-1" /> Aggiungi il primo cliente
            </Button>
          </Card>
        )}
        {clients.map((c) => (
          <Card
            key={c.id}
            className="p-5 hover:border-[#0F2C59] cursor-pointer transition-colors group relative"
            onClick={() => navigate(`/clienti/${c.id}`)}
            data-testid={`client-card-${c.id}`}
          >
            {/* Action buttons overlay - top-right */}
            <div className="absolute top-3 right-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={(e) => handleNewOffer(e, c)}
                className="p-1.5 rounded bg-white shadow-sm border border-slate-200 hover:bg-[#FACC15]/10 hover:border-[#FACC15] text-[#0F2C59]"
                title="Nuova offerta per questo cliente"
                data-testid={`client-new-offer-${c.id}`}
              >
                <FileText className="w-4 h-4" />
              </button>
              <button
                onClick={(e) => handleEdit(e, c)}
                className="p-1.5 rounded bg-white shadow-sm border border-slate-200 hover:bg-blue-50 hover:border-[#0F2C59] text-[#0F2C59]"
                title="Modifica cliente"
                data-testid={`client-edit-${c.id}`}
              >
                <Edit className="w-4 h-4" />
              </button>
              <button
                onClick={(e) => handleDelete(e, c)}
                className="p-1.5 rounded bg-white shadow-sm border border-slate-200 hover:bg-red-50 hover:border-red-300 text-red-500"
                title="Elimina cliente"
                data-testid={`client-delete-${c.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-md bg-[#E6F0FF] flex items-center justify-center flex-shrink-0">
                <Building2 className="w-5 h-5 text-[#0F2C59]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold text-[#0F2C59] truncate">{c.ragione_sociale}</div>
                {c.partita_iva && <div className="text-xs font-mono text-slate-500 mt-0.5">P.IVA {c.partita_iva}</div>}
                {c.comune && (
                  <div className="text-xs text-slate-500 mt-2 flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> {c.comune} {c.provincia && `(${c.provincia})`}
                  </div>
                )}
                <div className="mt-3 flex flex-wrap gap-1.5">
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${
                    c.livello_rischio === "alto" ? "bg-red-50 text-red-700 border-red-300" :
                    c.livello_rischio === "medio" ? "bg-orange-50 text-orange-700 border-orange-300" :
                    "bg-green-50 text-green-700 border-green-300"
                  }`}>Rischio {c.livello_rischio}</span>
                  {c.numero_dipendenti > 0 && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-300">
                      {c.numero_dipendenti} dip.
                    </span>
                  )}
                  {c.igiene_alimentare && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-cyan-50 text-cyan-700 border border-cyan-300">HACCP</span>
                  )}
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
